// Group membership rules. Membership is explicit (`item.groupId`); these
// functions only decide which explicit changes a gesture should produce.

import { applyOps, type BoardState, type Item, type Op, groupMembers } from '../board'
import { centre, containsPoint, contains, overlaps, pad, type Rect, round, union } from './geometry'

/** Room between a group's frame and the things inside it. */
export const GROUP_PAD = 24

/**
 * Whether frames shrink back to their members when members leave or move
 * (暫定, being tried both ways: a build with VITE_GROUP_FRAMES=fit shrinks).
 * Without it a frame only ever grows, and only a person resizes it smaller.
 * The same for people and the AI.
 */
export function framesShrink(): boolean {
  // Written out in full: the build replaces `import.meta.env.VITE_…` by its value.
  return import.meta.env.VITE_GROUP_FRAMES === 'fit'
}

/** The smallest frame a group may have: around all of its members. */
export function membersBounds(s: BoardState, groupId: string): Rect | null {
  const box = union(groupMembers(s, groupId))
  return box ? pad(box, GROUP_PAD) : null
}

/** Resizing a group never moves items and may not cut any member off. */
export function canResizeGroup(s: BoardState, groupId: string, next: Rect): boolean {
  const min = membersBounds(s, groupId)
  return !min || contains(next, min)
}

/** Items adopted when a group is drawn around an area: whatever has its centre inside. */
export function adoptedBy(s: BoardState, area: Rect): Item[] {
  return [...s.items.values()].filter((i) => i.kind !== 'group' && containsPoint(area, centre(i)))
}

/**
 * Membership changes after `moved` items were dropped at their new rects
 * (already applied to `after`). The rules (暫定, to be tuned):
 * - still overlapping its own group: stays, and the group grows to contain it;
 * - completely outside its group: leaves;
 * - not in a group and dropped with its centre inside one: joins, and the group
 *   grows to contain it.
 * Groups themselves never join anything (no nesting).
 */
export function membershipOnDrop(
  after: BoardState,
  moved: readonly string[],
  shrink = framesShrink(),
): Op[] {
  const ops: Op[] = []
  const grow = new Map<string, Rect>()
  /** Groups a moved thing was in or went into: their frames are fitted when frames shrink. */
  const touched = new Set<string>()
  const groups = [...after.items.values()].filter((i) => i.kind === 'group')
  const growTo = (g: Item, it: Item) => {
    const cur = grow.get(g.id) ?? g
    grow.set(g.id, union([cur, pad(it, GROUP_PAD)]) ?? cur)
  }
  for (const id of moved) {
    const it = after.items.get(id)
    if (!it || it.kind === 'group') continue
    const own = it.groupId ? after.items.get(it.groupId) : undefined
    if (own) touched.add(own.id)
    if (own) {
      if (overlaps(it, grow.get(own.id) ?? own)) {
        growTo(own, it)
        continue
      }
      ops.push({ type: 'item.update', id, patch: { groupId: null } })
    }
    const target = groups
      .filter((g) => g.id !== own?.id && containsPoint(grow.get(g.id) ?? g, centre(it)))
      .sort((a, b) => a.w * a.h - b.w * b.h)[0]
    if (target) {
      touched.add(target.id)
      ops.push({ type: 'item.update', id, patch: { groupId: target.id } })
      growTo(target, it)
    }
  }
  if (shrink) {
    // Each touched group's frame is exactly around its members after the drop.
    const groupOf = new Map<string, string | null>()
    for (const op of ops)
      if (op.type === 'item.update' && op.patch.groupId !== undefined)
        groupOf.set(op.id, op.patch.groupId)
    for (const gid of touched) {
      const members = [...after.items.values()].filter(
        (i) => (groupOf.has(i.id) ? groupOf.get(i.id) : i.groupId) === gid,
      )
      const box = union(members)
      if (box) grow.set(gid, pad(box, GROUP_PAD))
      else grow.delete(gid)
    }
  }
  for (const [gid, r] of grow) {
    const g = after.items.get(gid)
    if (!g) continue
    const next = round(r)
    if (next.x !== g.x || next.y !== g.y || next.w !== g.w || next.h !== g.h) {
      ops.push({ type: 'item.update', id: gid, patch: next })
    }
  }
  return ops
}

/**
 * Ops that put each group's frame around its members: grown to contain them
 * always, and shrunk to them when frames shrink (`framesShrink`). Empty
 * groups keep their frame.
 */
export function fitFrames(
  s: BoardState,
  groupIds: Iterable<string>,
  shrink = framesShrink(),
): Op[] {
  const ops: Op[] = []
  for (const gid of new Set(groupIds)) {
    const g = s.items.get(gid)
    const box = g?.kind === 'group' ? union(groupMembers(s, gid)) : null
    if (!g || !box) continue
    const need = pad(box, GROUP_PAD)
    const next = round(shrink ? need : (union([g, need]) ?? g))
    if (next.x !== g.x || next.y !== g.y || next.w !== g.w || next.h !== g.h)
      ops.push({ type: 'item.update', id: gid, patch: next })
  }
  return ops
}

/** A batch that creates things, with what the drop rule makes of where they landed. */
export function withMembership(before: BoardState, ops: Op[], now = Date.now()): Op[] {
  const created = ops.flatMap((o) =>
    o.type === 'item.create' && o.item.kind !== 'group' && !o.item.groupId ? [o.item.id] : [],
  )
  if (!created.length) return ops
  try {
    return [...ops, ...membershipOnDrop(applyOps(before, ops, now), created)]
  } catch {
    return ops
  }
}
